import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  styled,
  tableCellClasses,
  Button,
  Grid,
  CardContent,
  Card,
  TextField,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { isMobile } from "../../globalStyles/mobile-design";
import { formatKey } from "../../utils/utility";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--bg-secondary)",
    border: "1px solid var(--border-color)",
    padding: "6px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "6px",
  },
}));

const SECTION_MAX_WIDTH = 1200;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--bg-paper)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--bg-secondary)",
    border: "1px solid var(--border-color)",
  },
  "&:lastChild td, &:lastChild th": {
    border: 0,
  },
}));

const MappingColumns = ({
  title = "Mapping Columns",
  description = "Use the dropdowns to adjust and ensure everything lines up.",
  rows = [],
  dropdownOptions = [],
  onCancel,
  onProceed,
  onSaveAndProceed,
  previewData,
  mappingColumns,
  setMappingColumns,
  onReturnToTemplateSelection,
}) => {
  const addToMappingColumns = (key, column, value, operator) => {
    const existingIndex = mappingColumns.findIndex((item) => item.key === key);

    let obj = {
      key: key,
      column: column,
    };
    if (column === "user_constant") {
      obj.value = value;
      obj.type =
        mappingColumns[existingIndex]?.type ||
        mappingColumns.find((x) => x.key === key)?.column;
    }
    if (operator !== undefined) {
      obj.operator = operator;
    }

    if (existingIndex >= 0) {
      setMappingColumns(
        mappingColumns.map((x, i) =>
          i === existingIndex ? { ...x, ...obj } : x,
        ),
      );
    } else {
      setMappingColumns([...mappingColumns, obj]);
    }
  };

  const actionOperatorMap = {
    equals: "Equals",
    startsWith: "Starts With",
    endsWith: "Ends With",
    contains: "Contains",
    equalsIgnoreCase: "Equals (Case Insensitive)",
    startsWithIgnoreCase: "Starts With (Case Insensitive)",
    endsWithIgnoreCase: "Ends With (Case Insensitive)",
    containsIgnoreCase: "Contains (Case Insensitive)",
    combine: "Combine",
  };
  const actionOperators = Object.keys(actionOperatorMap);

  const getCombinedColumns = (row) =>
    (mappingColumns.find((x) => x.key === row)?.column || "")
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);

  const buildCombinedPreview = (row) =>
    getCombinedColumns(row).reduce((obj, col) => {
      obj[col] = previewData[col];
      return obj;
    }, {});

  const MobileCardView = ({ data }) => {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: SECTION_MAX_WIDTH,
          mx: "auto",
          padding: 0.5,
          maxHeight: "45vh",
          overflowY: "scroll",
        }}
      >
        {data?.map((row, index) => (
          <Card
            key={index}
            sx={{
              marginBottom: 2,
              backgroundColor: "var(--bg-paper)",
              borderRadius: 4,
              position: "relative",
            }}
          >
            <CardContent>
              <Box display="flex" flexDirection="column">
                <Grid container spacing={2} sx={{ marginTop: 1 }}>
                  <Grid item xs={6}>
                    <Typography
                      fontSize={14}
                      fontFamily="poppins"
                      color="var(--text-tertiary)"
                      fontWeight={600}
                      textAlign="left"
                    >
                      Import to RL
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      fontSize={14}
                      fontFamily="Poppins"
                      fontWeight={500}
                      noWrap
                      overflow="hidden"
                      textOverflow="ellipsis"
                      textAlign="left"
                    >
                      : {formatKey(row)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      fontSize={14}
                      fontFamily="poppins"
                      color="var(--text-tertiary)"
                      fontWeight={600}
                      textAlign="left"
                    >
                      Column Heading
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    {mappingColumns.find((x) => x.key === row)?.operator ===
                    "combine" ? (
                      <Select
                        size="small"
                        fullWidth
                        multiple
                        value={getCombinedColumns(row)}
                        renderValue={(selected) => selected.join(", ")}
                        onChange={(e) =>
                          addToMappingColumns(
                            row,
                            (Array.isArray(e.target.value)
                              ? e.target.value
                              : e.target.value.split(",")
                            ).join(","),
                          )
                        }
                      >
                        {dropdownOptions.map((option, idx) => (
                          <MenuItem key={option + " " + idx} value={option}>
                            <Checkbox
                              checked={getCombinedColumns(row).includes(
                                option,
                              )}
                            />
                            <ListItemText primary={option} />
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Select
                        size="small"
                        fullWidth
                        defaultValue={row.defaultOption || ""}
                        onChange={(e) =>
                          addToMappingColumns(row, e.target.value)
                        }
                        value={
                          mappingColumns.find((x) => x.key === row)?.column ||
                          ""
                        }
                      >
                        <MenuItem
                          key={row + "_default"}
                          value=""
                          style={{ color: "#757575" }}
                        >
                          --
                        </MenuItem>
                        <MenuItem key={"user_constant"} value="user_constant">
                          User Constant Input
                        </MenuItem>
                        {dropdownOptions.map((option, idx) => (
                          <MenuItem key={option + " " + idx} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </Grid>

                  <Grid item xs={6}>
                    <Typography
                      fontSize={14}
                      fontFamily="poppins"
                      color="var(--text-tertiary)"
                      fontWeight={600}
                      textAlign="left"
                    >
                      Preview
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    {mappingColumns.find((x) => x.key === row)?.operator ===
                    "combine" ? (
                      <Typography
                        fontSize={14}
                        fontFamily="Poppins"
                        color="#606060"
                        fontWeight={500}
                        noWrap
                        overflow="hidden"
                        textOverflow="ellipsis"
                        textAlign="left"
                      >
                        : {JSON.stringify(buildCombinedPreview(row))}
                      </Typography>
                    ) : mappingColumns.find((x) => x.key === row)?.column ===
                      "user_constant" ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={
                          mappingColumns.find((x) => x.key === row)?.value || ""
                        }
                        onChange={(e) =>
                          addToMappingColumns(
                            row,
                            "user_constant",
                            e.target.value,
                          )
                        }
                        placeholder="Enter constant value"
                      />
                    ) : (
                      <Typography
                        fontSize={14}
                        fontFamily="Poppins"
                        color="#606060"
                        fontWeight={500}
                        noWrap
                        overflow="hidden"
                        textOverflow="ellipsis"
                        textAlign="left"
                      >
                        :{" "}
                        {previewData[
                          mappingColumns.find((x) => x.key === row)?.value
                        ] || "--"}
                      </Typography>
                    )}
                  </Grid>

                  {row === "operator" &&
                    mappingColumns.find((x) => x.key === row)?.column && (
                      <>
                        <Grid item xs={6}>
                          <Typography
                            fontSize={14}
                            fontFamily="poppins"
                            color="var(--text-tertiary)"
                            fontWeight={600}
                            textAlign="left"
                          >
                            Operator
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Select
                            size="small"
                            fullWidth
                            value={
                              mappingColumns.find((x) => x.key === row)
                                ?.operator || ""
                            }
                            onChange={(e) =>
                              addToMappingColumns(
                                row,
                                mappingColumns.find((x) => x.key === row)
                                  ?.column,
                                undefined,
                                e.target.value,
                              )
                            }
                          >
                            <MenuItem value="">--</MenuItem>
                            {actionOperators.map((op) => (
                              <MenuItem key={op} value={op}>
                                {actionOperatorMap[op]}
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>
                      </>
                    )}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: 2,
      }}
    >
      <Box sx={{ flex: 1, overflowY: "auto", paddingBottom: "60px" }}>
        <Box sx={{ width: "100%", maxWidth: SECTION_MAX_WIDTH, mx: "auto" }}>
          <Typography
            fontFamily="poppins"
            fontWeight={500}
            fontSize={"16px"}
            color="var(--text-tertiary)"
          >
            {title}
          </Typography>
          <Typography
            fontFamily="poppins"
            fontWeight={500}
            fontSize="14px"
            color="#757575"
          >
            {description}
          </Typography>
        </Box>
        {isMobile() ? (
          <MobileCardView data={rows} />
        ) : (
          <TableContainer
            sx={{
              maxHeight: "55vh",
              overflowY: "auto",
              paddingBottom: 10,
              width: "100%",
              maxWidth: SECTION_MAX_WIDTH,
              mx: "auto",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>Import to RL</StyledTableCell>
                  <StyledTableCell>Operator</StyledTableCell>
                  <StyledTableCell>Column Heading</StyledTableCell>
                  <StyledTableCell>Preview</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell>{formatKey(row)}</StyledTableCell>
                    <StyledTableCell>
                      <Select
                        fullWidth
                        size="small"
                        value={
                          mappingColumns.find((x) => x.key === row)?.operator ||
                          ""
                        }
                        onChange={(e) =>
                          addToMappingColumns(
                            row,
                            mappingColumns.find((x) => x.key === row)?.column,
                            undefined,
                            e.target.value,
                          )
                        }
                      >
                        <MenuItem value="">--</MenuItem>
                        {actionOperators.map((op) => (
                          <MenuItem key={op} value={op}>
                            {actionOperatorMap[op]}
                          </MenuItem>
                        ))}
                      </Select>
                    </StyledTableCell>
                    <StyledTableCell>
                      {mappingColumns.find((x) => x.key === row)?.operator ===
                      "combine" ? (
                        <Select
                          fullWidth
                          size="small"
                          multiple
                          value={getCombinedColumns(row)}
                          renderValue={(selected) => selected.join(", ")}
                          onChange={(e) =>
                            addToMappingColumns(
                              row,
                              (Array.isArray(e.target.value)
                                ? e.target.value
                                : e.target.value.split(",")
                              ).join(","),
                            )
                          }
                        >
                          {dropdownOptions
                            .filter((x) => x)
                            .map((option, idx) => (
                              <MenuItem key={idx} value={option}>
                                <Checkbox
                                  checked={getCombinedColumns(row).includes(
                                    option,
                                  )}
                                />
                                <ListItemText primary={option} />
                              </MenuItem>
                            ))}
                        </Select>
                      ) : (
                        <Select
                          fullWidth
                          size="small"
                          value={
                            mappingColumns.find((x) => x.key === row)
                              ?.column || ""
                          }
                          onChange={(e) =>
                            addToMappingColumns(row, e.target.value)
                          }
                        >
                          <MenuItem value="">--</MenuItem>
                          <MenuItem value="user_constant">
                            User Constant Input
                          </MenuItem>
                          {dropdownOptions
                            .filter((x) => x)
                            .map((option, idx) => (
                              <MenuItem key={idx} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                        </Select>
                      )}
                    </StyledTableCell>
                    <StyledTableCell sx={{ maxWidth: "200px" }}>
                      {mappingColumns.find((x) => x.key === row)?.operator ===
                      "combine" ? (
                        JSON.stringify(buildCombinedPreview(row))
                      ) : mappingColumns.find((x) => x.key === row)?.column ===
                        "user_constant" ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={
                            mappingColumns.find((x) => x.key === row)?.value ||
                            ""
                          }
                          onChange={(e) =>
                            addToMappingColumns(
                              row,
                              "user_constant",
                              e.target.value,
                            )
                          }
                          placeholder="Enter constant value"
                        />
                      ) : (
                        previewData[
                          mappingColumns.find((x) => x.key === row)?.column
                        ] || "--"
                      )}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Buttons Section */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          background: "var(--bg-paper)",
          padding: "10px 0",
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          zIndex: 10,
          borderTop: "1px solid #EAECF0",
          width: "100%",
          maxWidth: SECTION_MAX_WIDTH,
          mx: "auto",
        }}
      >
        <Button
          variant="contained"
          sx={{ textTransform: "none", alignSelf: "flex-start" }}
          onClick={() => onReturnToTemplateSelection()}
        >
          <Typography
            fontSize={14}
            fontWeight={500}
            sx={{
              color: "var(--text-primary)",
            }}
            fontFamily={"poppins"}
          >
            Return to Template Selection
          </Typography>
        </Button>
        <Box
          sx={{
            padding: "10px 0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            sx={{ textTransform: "none" }}
            onClick={onCancel}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            sx={{ textTransform: "none" }}
            onClick={onSaveAndProceed}
            color="primary"
          >
            Save & Proceed
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MappingColumns;
